import { useEffect, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { AsyncStatus, Button, Field } from "../components";

export interface RedemptionProductOption {
  productId: string;
  code: string;
  name: string;
}

interface RedemptionResult {
  redemption: {
    redemptionId: string;
    productCode: string;
    productName: string;
    durationDays: number;
    resultingExpiry: string;
  };
}

interface AdminRedemptionCodeView {
  id: string;
  name: string;
  codeHint: string;
  productId: string;
  productCode: string;
  productName: string;
  durationDays: number;
  expiresAt: string;
  maxRedemptions: number | null;
  maxRedemptionsPerUser: number;
  redemptionCount: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback;
}

function redemptionErrorMessage(caught: unknown): string {
  if (caught instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      REDEMPTION_CODE_INVALID: "兑换码无效、已停用或已过期",
      REDEMPTION_CODE_USER_LIMIT_REACHED: "你的账号已达到该兑换码的兑换次数上限",
      REDEMPTION_CODE_RATE_LIMITED: "尝试次数过多，请一分钟后再试",
      ENTITLEMENT_NOT_REDEEMABLE: "当前权益状态不允许使用兑换码，请联系支持",
    };
    return messages[caught.code] ?? caught.message;
  }
  return errorMessage(caught, "兑换失败，请稍后重试");
}

function redemptionCodeStatus(redemptionCode: AdminRedemptionCodeView): string {
  if (!redemptionCode.active) {
    return "已停用";
  }
  if (new Date(redemptionCode.expiresAt) <= new Date()) {
    return "已过期";
  }
  if (
    redemptionCode.maxRedemptions !== null &&
    redemptionCode.redemptionCount >= redemptionCode.maxRedemptions
  ) {
    return "已用完";
  }
  return "有效";
}

function formatLocalDateTime(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function initialExpiry(): string {
  return formatLocalDateTime(new Date(Date.now() + 7 * 86_400_000));
}

function generateCode(): string {
  const bytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(bytes);
  return `BOMANA-${Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  )
    .join("")
    .toUpperCase()}`;
}

export function RedemptionCodeForm({
  onRedeemed,
}: {
  onRedeemed: () => void | Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedCode = code.trim().toUpperCase();
    if (normalizedCode.length < 8) {
      setError("请输入完整兑换码");
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await apiRequest<RedemptionResult>(
        "/api/account/redemption-codes/redeem",
        {
          method: "POST",
          body: JSON.stringify({ code: normalizedCode }),
        },
      );
      setCode("");
      setSuccess(
        `已兑换 ${result.redemption.productName} ${result.redemption.durationDays} 天，有效期至 ${new Date(
          result.redemption.resultingExpiry,
        ).toLocaleString("zh-CN")}`,
      );
      await onRedeemed();
    } catch (caught) {
      setError(redemptionErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="dashboard-card redemption-card">
      <div className="card-title-row">
        <div>
          <span className="card-kicker">兑换码</span>
          <h2>兑换权益</h2>
          <p>
            输入管理员发放的兑换码。每个兑换码对同一账号可能有次数限制；已有同一产品权益会从当前到期日继续累加。
          </p>
        </div>
      </div>
      <form className="referral-claim" onSubmit={submit}>
        <Field label="兑换码">
          <input
            autoComplete="off"
            maxLength={64}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="例如 BOMANA-XXXXXXXX"
            required
            value={code}
          />
        </Field>
        <Button busy={busy} busyLabel="兑换中" type="submit">
          确认兑换
        </Button>
      </form>
      {error ? <AsyncStatus tone="error">{error}</AsyncStatus> : null}
      {success ? <AsyncStatus tone="success">{success}</AsyncStatus> : null}
    </article>
  );
}

export function AdminRedemptionCodesPanel({
  products,
}: {
  products: RedemptionProductOption[];
}) {
  const [redemptionCodes, setRedemptionCodes] = useState<
    AdminRedemptionCodeView[]
  >([]);
  const [selectedId, setSelectedId] = useState("");
  const [code, setCode] = useState(generateCode);
  const [name, setName] = useState("Bomana 限时兑换");
  const [productId, setProductId] = useState(products[0]?.productId ?? "");
  const [durationDays, setDurationDays] = useState("7");
  const [expiresAt, setExpiresAt] = useState(initialExpiry);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [maxRedemptionsPerUser, setMaxRedemptionsPerUser] = useState("1");
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [createdCode, setCreatedCode] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest<{
        redemptionCodes: AdminRedemptionCodeView[];
      }>("/api/admin/redemption-codes");
      setRedemptionCodes(result.redemptionCodes);
    } catch (caught) {
      setError(errorMessage(caught, "兑换码列表加载失败"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!productId && products[0]) {
      setProductId(products[0].productId);
    }
  }, [productId, products]);

  const reset = () => {
    setSelectedId("");
    setCode(generateCode());
    setName("Bomana 限时兑换");
    setProductId(products[0]?.productId ?? "");
    setDurationDays("7");
    setExpiresAt(initialExpiry());
    setMaxRedemptions("");
    setMaxRedemptionsPerUser("1");
    setActive(true);
    setCreatedCode("");
    setError("");
  };

  const edit = (redemptionCode: AdminRedemptionCodeView) => {
    setSelectedId(redemptionCode.id);
    setCode("");
    setName(redemptionCode.name);
    setProductId(redemptionCode.productId);
    setDurationDays(String(redemptionCode.durationDays));
    setExpiresAt(formatLocalDateTime(new Date(redemptionCode.expiresAt)));
    setMaxRedemptions(
      redemptionCode.maxRedemptions === null
        ? ""
        : String(redemptionCode.maxRedemptions),
    );
    setMaxRedemptionsPerUser(String(redemptionCode.maxRedemptionsPerUser));
    setActive(redemptionCode.active);
    setCreatedCode("");
    setError("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const days = Number(durationDays);
    const total = maxRedemptions.trim() === "" ? null : Number(maxRedemptions);
    const perUser = Number(maxRedemptionsPerUser);
    const expiry = new Date(expiresAt);
    if (
      !productId ||
      !Number.isInteger(days) ||
      days < 1 ||
      (total !== null && (!Number.isInteger(total) || total < 1)) ||
      !Number.isInteger(perUser) ||
      perUser < 1 ||
      !Number.isFinite(expiry.getTime()) ||
      expiry <= new Date() ||
      (!selectedId && code.trim().length < 8)
    ) {
      setError("请检查兑换码、内容、时长、有效期和兑换次数");
      return;
    }
    setBusy(true);
    setError("");
    setCreatedCode("");
    try {
      const body = {
        name: name.trim(),
        productId,
        durationDays: days,
        expiresAt: expiry.toISOString(),
        maxRedemptions: total,
        maxRedemptionsPerUser: perUser,
        active,
      };
      const result = await apiRequest<{
        redemptionCode: AdminRedemptionCodeView;
        code?: string;
      }>(
        selectedId
          ? `/api/admin/redemption-codes/${selectedId}`
          : "/api/admin/redemption-codes",
        {
          method: selectedId ? "PUT" : "POST",
          body: JSON.stringify(
            selectedId ? body : { ...body, code: code.trim().toUpperCase() },
          ),
        },
      );
      if (result.code) {
        setCreatedCode(result.code);
      }
      await load();
      if (selectedId) {
        edit(result.redemptionCode);
      }
    } catch (caught) {
      setError(errorMessage(caught, "兑换码保存失败"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-workspace redemption-admin-workspace">
      <article className="dashboard-card">
        <div className="card-title-row">
          <div>
            <span className="card-kicker">运营工具</span>
            <h2>兑换码管理</h2>
            <p>代码创建后不再展示明文；可继续修改兑换内容、有效期和次数限制。</p>
          </div>
          <Button onClick={reset} type="button" variant="secondary">
            创建兑换码
          </Button>
        </div>

        <form className="product-editor" onSubmit={submit}>
          <div className="product-editor-grid">
            {!selectedId ? (
              <Field label="兑换码" hint="创建成功后仅显示一次明文">
                <input
                  autoComplete="off"
                  maxLength={64}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  required
                  value={code}
                />
              </Field>
            ) : null}
            <Field label="活动名称">
              <input
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
                required
                value={name}
              />
            </Field>
            <Field label="兑换内容">
              <select
                onChange={(event) => setProductId(event.target.value)}
                required
                value={productId}
              >
                {products.map((product) => (
                  <option key={product.productId} value={product.productId}>
                    {product.name} · {product.code}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="权益天数">
              <input
                min="1"
                onChange={(event) => setDurationDays(event.target.value)}
                required
                type="number"
                value={durationDays}
              />
            </Field>
            <Field label="有效期至">
              <input
                onChange={(event) => setExpiresAt(event.target.value)}
                required
                type="datetime-local"
                value={expiresAt}
              />
            </Field>
            <Field label="总兑换量" hint="留空表示无限">
              <input
                min="1"
                onChange={(event) => setMaxRedemptions(event.target.value)}
                placeholder="留空表示无限"
                type="number"
                value={maxRedemptions}
              />
            </Field>
            <Field label="每用户最多兑换">
              <input
                min="1"
                onChange={(event) =>
                  setMaxRedemptionsPerUser(event.target.value)
                }
                required
                type="number"
                value={maxRedemptionsPerUser}
              />
            </Field>
          </div>
          <label className="admin-switch">
            <input
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              type="checkbox"
            />
            <span>{active ? "启用" : "停用"}</span>
          </label>
          <Button busy={busy} busyLabel="保存中" type="submit">
            {selectedId ? "保存兑换码" : "创建并显示兑换码"}
          </Button>
        </form>
        {createdCode ? (
          <AsyncStatus tone="success">
            创建成功，请立即保存兑换码：<code>{createdCode}</code>
          </AsyncStatus>
        ) : null}
        {error ? <AsyncStatus tone="error">{error}</AsyncStatus> : null}
      </article>

      <article className="dashboard-card">
        <div className="card-title-row">
          <div>
            <span className="card-kicker">已配置</span>
            <h2>兑换活动</h2>
          </div>
          <Button onClick={() => void load()} type="button" variant="secondary">
            刷新
          </Button>
        </div>
        {loading ? <AsyncStatus tone="loading">正在读取兑换码…</AsyncStatus> : null}
        {!loading && redemptionCodes.length === 0 ? <p>尚未创建兑换码。</p> : null}
        <div className="admin-product-list">
          {redemptionCodes.map((redemptionCode) => (
            <article className="admin-product-row" key={redemptionCode.id}>
              <div>
                <strong>{redemptionCode.name}</strong>
                <p>
                  尾号 {redemptionCode.codeHint} · {redemptionCode.productName} ·{" "}
                  {redemptionCode.durationDays} 天
                </p>
                <small>
                  已兑换 {redemptionCode.redemptionCount} /{" "}
                  {redemptionCode.maxRedemptions ?? "无限"} · 每用户{" "}
                  {redemptionCode.maxRedemptionsPerUser} 次 · 有效期至{" "}
                  {new Date(redemptionCode.expiresAt).toLocaleString("zh-CN")} ·{" "}
                  {redemptionCodeStatus(redemptionCode)}
                </small>
              </div>
              <Button
                onClick={() => edit(redemptionCode)}
                type="button"
                variant="secondary"
              >
                编辑
              </Button>
            </article>
          ))}
        </div>
      </article>
    </div>
  );
}
